import { BrandSocialAssetSpec, BrandDisplayBannerSpec } from '@/types/brand';

// Default Social Platform Specifications
export const DEFAULT_SOCIAL_ASSETS: BrandSocialAssetSpec[] = [
  {
    id: 'preset-linkedin',
    platform: 'LinkedIn',
    postSize: '1200 x 627 px',
    altSize: '1584 x 396 px (Banner)',
    storySize: 'N/A',
    coverSize: '1128 x 191 px',
    textLegibility: '24pt for Headlines, 14pt for Body',
    directive: 'Center 1200px (Avoid edges for text). Logo placement: top-left or bottom-left. Maintain 60px padding from all edges.',
    templates: [],
    previewImageUrl: '/images/social-defaults/linkedin-default.jpg',
  },
  {
    id: 'preset-x',
    platform: 'X',
    postSize: '1600 x 900 px',
    altSize: '1500 x 500 px (Header)',
    storySize: 'N/A',
    coverSize: '1500 x 500 px',
    textLegibility: '32pt for Headlines, 18pt for Body',
    directive: 'Center 1000px horizontally. Profile pic overlaps header on left. Keep important content right-of-center.',
    templates: [],
    previewImageUrl: '/images/social-defaults/twitter-default.jpg',
  },
  {
    id: 'preset-instagram',
    platform: 'Instagram',
    postSize: '1080 x 1080 px (1:1)',
    altSize: '1080 x 566 px (Landscape)',
    storySize: '1080 x 1920 px (9:16)',
    reelSize: '1080 x 1920 px (9:16)',
    coverSize: 'N/A',
    textLegibility: '48pt for Stories, 24pt for Posts',
    directive: 'Keep text within inner 80% to avoid UI overlays. Stories: avoid top 150px (username) and bottom 200px (CTA buttons).',
    templates: [],
    previewImageUrl: '/images/social-defaults/instagram-default.jpg',
  },
  {
    id: 'preset-facebook',
    platform: 'Facebook',
    postSize: '1200 x 630 px',
    altSize: '1200 x 1200 px (Square)',
    storySize: '1080 x 1920 px',
    coverSize: '820 x 312 px',
    textLegibility: '30pt for Headlines, 16pt for Body',
    directive: 'Center 640px to accommodate mobile crop. Cover: profile pic overlaps left side, keep key content centered.',
    templates: [],
    previewImageUrl: '/images/social-defaults/facebook-default.jpg',
  },
  {
    id: 'preset-youtube',
    platform: 'YouTube',
    postSize: '1280 x 720 px (Thumbnail)',
    altSize: '2560 x 1440 px (Channel Art)',
    storySize: '1080 x 1920 px (Shorts)',
    reelSize: '1080 x 1920 px (Shorts)',
    coverSize: '2560 x 1440 px',
    textLegibility: '96pt for Thumbnails, 48pt for Overlays',
    directive: 'Thumbnails: faces and bold text perform best. Channel art safe area: 1546 x 423 px center. Avoid corners.',
    templates: [],
    previewImageUrl: '/images/social-defaults/youtube-default.jpg',
  },
  {
    id: 'preset-tiktok',
    platform: 'TikTok',
    postSize: '1080 x 1920 px (9:16)',
    altSize: 'N/A',
    storySize: '1080 x 1920 px',
    reelSize: '1080 x 1920 px',
    coverSize: 'N/A',
    textLegibility: '48pt minimum for in-video text',
    directive: 'Avoid top 150px (username/music) and bottom 280px (engagement buttons/description). Center text in middle 60%.',
    templates: [],
    previewImageUrl: '/images/social-defaults/tiktok-default.jpg',
  },
  {
    id: 'preset-pinterest',
    platform: 'Pinterest',
    postSize: '1000 x 1500 px (2:3)',
    altSize: '1000 x 1000 px (Square)',
    storySize: '1080 x 1920 px (Idea Pins)',
    coverSize: 'N/A',
    textLegibility: '36pt for Headlines, 18pt for Body',
    directive: 'Vertical pins perform best. Text overlay in top or bottom third. High-contrast colors for readability.',
    templates: [],
    previewImageUrl: '/images/social-defaults/pinterest-default.jpg',
  },
  {
    id: 'preset-threads',
    platform: 'Threads',
    postSize: '1080 x 1080 px',
    altSize: '1080 x 566 px (Landscape)',
    storySize: 'N/A',
    coverSize: 'N/A',
    textLegibility: '24pt for Post Images',
    directive: 'Similar to Instagram feed. Keep important content centered. Text-based posts often outperform images.',
    templates: [],
    previewImageUrl: '/images/social-defaults/threads-default.jpg',
  },
];

// Default IAB Display Banner Specifications
export const DEFAULT_DISPLAY_BANNERS: BrandDisplayBannerSpec[] = [
  // Desktop/Universal Banners
  {
    id: 'preset-mrec',
    name: 'Medium Rectangle (MREC)',
    dimensions: '300 x 250 px',
    maxMessaging: 'Headline: 25 chars | Body: 70 chars | CTA: 15 chars',
    textLegibility: 'Headline: 18-24pt Bold | Body: 12-14pt | CTA: 14pt Bold',
    safeZonePolicy: '15px padding all edges. Logo in corner (max 60x40px). CTA button bottom-right or center-bottom.',
    aspectRatio: 1.2,
    category: 'desktop',
  },
  {
    id: 'preset-leaderboard',
    name: 'Leaderboard',
    dimensions: '728 x 90 px',
    maxMessaging: 'Headline: 30 chars | Body: 50 chars | CTA: 12 chars',
    textLegibility: 'Headline: 24-28pt Bold | Body: 14-16pt | CTA: 14pt Bold',
    safeZonePolicy: 'Center content in 650px. Logo left (max 80x50px). CTA right side. Avoid outer 40px edges.',
    aspectRatio: 8.09,
    category: 'desktop',
  },
  {
    id: 'preset-wide-skyscraper',
    name: 'Wide Skyscraper',
    dimensions: '160 x 600 px',
    maxMessaging: 'Headline: 20 chars | Body: 60 chars | CTA: 10 chars',
    textLegibility: 'Headline: 18-22pt Bold | Body: 11-13pt | CTA: 12pt Bold',
    safeZonePolicy: '10px horizontal padding. Logo top (max 120x40px). Stack content vertically. CTA bottom 80px.',
    aspectRatio: 0.27,
    category: 'desktop',
  },
  {
    id: 'preset-large-rectangle',
    name: 'Large Rectangle',
    dimensions: '336 x 280 px',
    maxMessaging: 'Headline: 30 chars | Body: 90 chars | CTA: 15 chars',
    textLegibility: 'Headline: 20-26pt Bold | Body: 13-15pt | CTA: 14pt Bold',
    safeZonePolicy: '20px padding all edges. Logo top-left or top-right. CTA center-bottom with 25px margin.',
    aspectRatio: 1.2,
    category: 'desktop',
  },
  {
    id: 'preset-billboard',
    name: 'Billboard',
    dimensions: '970 x 250 px',
    maxMessaging: 'Headline: 40 chars | Body: 100 chars | CTA: 15 chars',
    textLegibility: 'Headline: 32-40pt Bold | Body: 16-18pt | CTA: 16pt Bold',
    safeZonePolicy: 'Center content in 800px. Logo on left or integrated. Multiple CTAs allowed. Avoid outer 85px.',
    aspectRatio: 3.88,
    category: 'desktop',
  },
  {
    id: 'preset-large-leaderboard',
    name: 'Large Leaderboard',
    dimensions: '970 x 90 px',
    maxMessaging: 'Headline: 35 chars | Body: 60 chars | CTA: 12 chars',
    textLegibility: 'Headline: 26-32pt Bold | Body: 14-16pt | CTA: 14pt Bold',
    safeZonePolicy: 'Center content in 850px. Logo left (max 100x60px). CTA right. Keep text vertically centered.',
    aspectRatio: 10.78,
    category: 'desktop',
  },
  {
    id: 'preset-half-page',
    name: 'Half Page (Monster)',
    dimensions: '300 x 600 px',
    maxMessaging: 'Headline: 30 chars | Body: 120 chars | CTA: 15 chars',
    textLegibility: 'Headline: 24-30pt Bold | Body: 14-16pt | CTA: 16pt Bold',
    safeZonePolicy: '20px padding all edges. Logo top (max 150x50px). Stack content. CTA bottom 60px with full width.',
    aspectRatio: 0.5,
    category: 'desktop',
  },
  {
    id: 'preset-portrait',
    name: 'Portrait',
    dimensions: '300 x 1050 px',
    maxMessaging: 'Headline: 25 chars | Body: 150 chars | CTA: 12 chars (multiple CTAs allowed)',
    textLegibility: 'Headline: 22-28pt Bold | Body: 13-15pt | CTA: 14pt Bold',
    safeZonePolicy: '20px horizontal padding. Divide into 3-4 visual sections. Logo top, CTA bottom 80px.',
    aspectRatio: 0.29,
    category: 'desktop',
  },
  {
    id: 'preset-skyscraper',
    name: 'Skyscraper',
    dimensions: '120 x 600 px',
    maxMessaging: 'Headline: 15 chars | Body: 40 chars | CTA: 8 chars',
    textLegibility: 'Headline: 14-18pt Bold | Body: 10-12pt | CTA: 11pt Bold',
    safeZonePolicy: '8px horizontal padding. Very limited space—prioritize logo + single message + CTA.',
    aspectRatio: 0.2,
    category: 'desktop',
  },
  {
    id: 'preset-square',
    name: 'Square',
    dimensions: '250 x 250 px',
    maxMessaging: 'Headline: 20 chars | Body: 50 chars | CTA: 12 chars',
    textLegibility: 'Headline: 18-22pt Bold | Body: 12-14pt | CTA: 13pt Bold',
    safeZonePolicy: '15px padding all edges. Logo top-left (max 50x35px). CTA bottom center.',
    aspectRatio: 1.0,
    category: 'desktop',
  },
  {
    id: 'preset-small-square',
    name: 'Small Square',
    dimensions: '200 x 200 px',
    maxMessaging: 'Headline: 15 chars | CTA: 10 chars (no body copy)',
    textLegibility: 'Headline: 16-20pt Bold | CTA: 12pt Bold',
    safeZonePolicy: '12px padding. Minimal elements only—logo + headline + CTA. No body text.',
    aspectRatio: 1.0,
    category: 'desktop',
  },
  {
    id: 'preset-button-1',
    name: 'Button 1',
    dimensions: '120 x 90 px',
    maxMessaging: 'CTA only: 8 chars max',
    textLegibility: 'CTA: 11-13pt Bold',
    safeZonePolicy: 'Logo OR text only. 5px padding. Single action focus.',
    aspectRatio: 1.33,
    category: 'desktop',
  },
  {
    id: 'preset-button-2',
    name: 'Button 2',
    dimensions: '120 x 60 px',
    maxMessaging: 'CTA only: 8 chars max',
    textLegibility: 'CTA: 10-12pt Bold',
    safeZonePolicy: 'Extremely limited. Logo or text, not both. 4px padding.',
    aspectRatio: 2.0,
    category: 'desktop',
  },
  // Mobile Banners
  {
    id: 'preset-mobile-leaderboard',
    name: 'Mobile Leaderboard',
    dimensions: '320 x 50 px',
    maxMessaging: 'Headline: 20 chars | CTA: 8 chars (no body)',
    textLegibility: 'Headline: 14-16pt Bold | CTA: 12pt Bold',
    safeZonePolicy: 'Logo left (max 40x30px). Text center. CTA right. 8px edge padding.',
    aspectRatio: 6.4,
    category: 'mobile',
  },
  {
    id: 'preset-mobile-banner',
    name: 'Mobile Banner',
    dimensions: '300 x 50 px',
    maxMessaging: 'Headline: 18 chars | CTA: 8 chars',
    textLegibility: 'Headline: 13-15pt Bold | CTA: 11pt Bold',
    safeZonePolicy: 'Logo left (max 35x28px). Single line text. CTA far right.',
    aspectRatio: 6.0,
    category: 'mobile',
  },
  {
    id: 'preset-large-mobile-banner',
    name: 'Large Mobile Banner',
    dimensions: '320 x 100 px',
    maxMessaging: 'Headline: 25 chars | Body: 35 chars | CTA: 10 chars',
    textLegibility: 'Headline: 16-18pt Bold | Body: 12pt | CTA: 13pt Bold',
    safeZonePolicy: 'Logo top-left. Two-line layout allowed. CTA bottom-right. 10px padding.',
    aspectRatio: 3.2,
    category: 'mobile',
  },
  {
    id: 'preset-smartphone-banner',
    name: 'Smartphone Banner',
    dimensions: '300 x 250 px (Mobile MREC)',
    maxMessaging: 'Headline: 25 chars | Body: 60 chars | CTA: 12 chars',
    textLegibility: 'Headline: 20-24pt Bold | Body: 13-15pt | CTA: 14pt Bold',
    safeZonePolicy: 'Same as desktop MREC. 15px padding. Design for touch—CTA min 44px height.',
    aspectRatio: 1.2,
    category: 'mobile',
  },
  {
    id: 'preset-mobile-interstitial',
    name: 'Mobile Interstitial',
    dimensions: '320 x 480 px',
    maxMessaging: 'Headline: 30 chars | Body: 80 chars | CTA: 15 chars',
    textLegibility: 'Headline: 28-34pt Bold | Body: 16-18pt | CTA: 18pt Bold',
    safeZonePolicy: 'Full screen. Close button space top-right (50x50px). CTA center-bottom 80px. 25px margins.',
    aspectRatio: 0.67,
    category: 'mobile',
  },
  {
    id: 'preset-tablet-interstitial',
    name: 'Tablet Interstitial',
    dimensions: '768 x 1024 px',
    maxMessaging: 'Headline: 40 chars | Body: 120 chars | CTA: 18 chars',
    textLegibility: 'Headline: 36-44pt Bold | Body: 18-22pt | CTA: 20pt Bold',
    safeZonePolicy: 'Full screen. Close button top-right (60x60px). Center content in 600px width. CTA bottom 100px.',
    aspectRatio: 0.75,
    category: 'mobile',
  },
  // Video/Rich Media Banners
  {
    id: 'preset-video-rectangle',
    name: 'Video Rectangle',
    dimensions: '640 x 360 px (16:9)',
    maxMessaging: 'Overlay: 20 chars | End card: 30 chars + CTA',
    textLegibility: 'Overlay: 18-22pt Bold (high contrast) | End card: 24-28pt',
    safeZonePolicy: 'Video controls bottom 50px. Logo watermark top-left (subtle). End card CTA center.',
    aspectRatio: 1.78,
    category: 'video',
  },
  {
    id: 'preset-video-skyscraper',
    name: 'Video Skyscraper',
    dimensions: '300 x 600 px (Video)',
    maxMessaging: 'Overlay: 15 chars | End card: 25 chars + CTA',
    textLegibility: 'Overlay: 16-20pt Bold | End card: 22-26pt',
    safeZonePolicy: 'Vertical video format. Controls bottom 60px. Logo top. End card CTA bottom third.',
    aspectRatio: 0.5,
    category: 'video',
  },
  // Native/Social Banners
  {
    id: 'preset-native-ad-unit',
    name: 'Native Ad Unit',
    dimensions: '1200 x 627 px',
    maxMessaging: 'Headline: 50 chars | Description: 150 chars | CTA: 15 chars',
    textLegibility: 'Headline: 24-30pt Bold | Description: 16-18pt | CTA: 16pt Bold',
    safeZonePolicy: 'Image-focused. Text overlay bottom 30% with gradient. Logo bottom-left. CTA bottom-right.',
    aspectRatio: 1.91,
    category: 'native',
  },
  {
    id: 'preset-sponsored-content-card',
    name: 'Sponsored Content Card',
    dimensions: '600 x 600 px',
    maxMessaging: 'Headline: 40 chars | Body: 100 chars | CTA: 12 chars',
    textLegibility: 'Headline: 26-32pt Bold | Body: 16-18pt | CTA: 16pt Bold',
    safeZonePolicy: 'Square format. Image top 60%. Text bottom 40%. Logo corner. CTA below text.',
    aspectRatio: 1.0,
    category: 'native',
  },
];
